import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Paper,
  Divider,
  Stack,
  IconButton,
  Chip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { NotificationTemplate } from '../../../types/notification-admin';
import { NotificationPriority } from '../../../types/user';

interface TemplatePreviewProps {
  open: boolean;
  template: NotificationTemplate | null;
  onClose: () => void;
}

/**
 * TemplatePreview Component
 * Displays a live preview of a notification template with variable substitution
 */
export const TemplatePreview: React.FC<TemplatePreviewProps> = ({
  open,
  template,
  onClose,
}) => {
  const [variableValues, setVariableValues] = useState<Record<string, string>>({});
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');

  /**
   * Initialize variable values when template changes
   */
  useEffect(() => {
    if (template) {
      const initialValues: Record<string, string> = {};
      template.variables.forEach((variable) => {
        initialValues[variable] = getSampleValue(variable);
      });
      setVariableValues(initialValues);
    }
  }, [template]);

  /**
   * Update preview when template or variable values change
   */
  useEffect(() => {
    if (template) {
      let title = template.title;
      let message = template.message;

      // Replace all variables with their values
      Object.entries(variableValues).forEach(([key, value]) => {
        const placeholder = `{{${key}}}`;
        const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
        title = title.replace(regex, value || `{{${key}}}`);
        message = message.replace(regex, value || `{{${key}}}`);
      });

      setPreviewTitle(title);
      setPreviewMessage(message);
    }
  }, [template, variableValues]);

  /**
   * Get sample value for a variable based on its name
   */
  const getSampleValue = (variable: string): string => {
    const lower = variable.toLowerCase();

    // User-related
    if (lower.includes('username') || lower.includes('user_name')) {
      return 'John Smith';
    }
    if (lower.includes('useremail') || lower.includes('user_email')) {
      return 'john.smith@example.com';
    }

    // Person-related
    if (lower.includes('personname') || lower.includes('person_name')) {
      return 'Jane Doe';
    }

    // Content-related
    if (lower.includes('itemname') || lower.includes('item_name')) {
      return 'Family Photo Album';
    }
    if (lower.includes('contenttype') || lower.includes('content_type')) {
      return 'photo';
    }
    if (lower.includes('documentname') || lower.includes('document_name')) {
      return 'Birth Certificate 1920.pdf';
    }

    // Relationship-related
    if (lower.includes('relationship')) {
      return 'Father of Sarah Johnson';
    }
    if (lower.includes('newrole') || lower.includes('new_role')) {
      return 'Editor';
    }

    // Date/Time-related
    if (lower.includes('daysaway') || lower.includes('days_away')) {
      return 'in 3 days';
    }
    if (lower.includes('duedate') || lower.includes('due_date')) {
      return 'December 25, 2024';
    }
    if (lower.includes('maintenancetime') || lower.includes('maintenance_time')) {
      return 'Sunday, 2:00 AM - 4:00 AM EST';
    }

    // Counts
    if (lower.includes('photocount') || lower.includes('photo_count')) {
      return '5';
    }
    if (lower.includes('percentcomplete') || lower.includes('percent_complete')) {
      return '75';
    }

    // Messages and text
    if (lower.includes('reason')) {
      return 'Contains inappropriate content';
    }
    if (lower.includes('topic')) {
      return 'German ancestry records';
    }
    if (lower.includes('taskname') || lower.includes('task_name')) {
      return 'Review family tree edits';
    }
    if (lower.includes('projectname') || lower.includes('project_name')) {
      return 'Smith Family History Project';
    }
    if (lower.includes('eventname') || lower.includes('event_name')) {
      return 'Wedding';
    }

    // Broadcast-related
    if (lower.includes('broadcasttitle') || lower.includes('broadcast_title')) {
      return 'Important Announcement';
    }
    if (lower.includes('broadcastmessage') || lower.includes('broadcast_message')) {
      return 'Please review the updated community guidelines.';
    }
    if (lower.includes('updatemessage') || lower.includes('update_message')) {
      return 'New features have been added to improve your experience.';
    }

    // Feature-related
    if (lower.includes('featurename') || lower.includes('feature_name')) {
      return 'Advanced Search';
    }
    if (lower.includes('featuredescription') || lower.includes('feature_description')) {
      return 'Find ancestors faster with our new advanced search filters.';
    }

    // Default
    return `[${variable}]`;
  };

  /**
   * Reset variable values to sample values
   */
  const handleReset = () => {
    if (template) {
      const resetValues: Record<string, string> = {};
      template.variables.forEach((variable) => {
        resetValues[variable] = getSampleValue(variable);
      });
      setVariableValues(resetValues);
    }
  };

  /**
   * Get priority color
   */
  const getPriorityColor = (
    priority: NotificationPriority
  ): 'error' | 'warning' | 'info' | 'default' => {
    switch (priority) {
      case NotificationPriority.CRITICAL:
        return 'error';
      case NotificationPriority.HIGH:
        return 'warning';
      case NotificationPriority.NORMAL:
        return 'info';
      case NotificationPriority.LOW:
        return 'default';
      default:
        return 'default';
    }
  };

  if (!template) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6">Template Preview</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          {/* Template Info */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Template Information
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip label={`Version ${template.version}`} size="small" />
              <Chip
                label={template.priority}
                size="small"
                color={getPriorityColor(template.priority)}
              />
              <Chip
                label={template.enabled ? 'Enabled' : 'Disabled'}
                size="small"
                color={template.enabled ? 'success' : 'default'}
              />
            </Box>
          </Box>

          <Divider />

          {/* Variable Inputs */}
          {template.variables.length > 0 && (
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                  Variable Values
                </Typography>
                <Button
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={handleReset}
                >
                  Reset to Sample
                </Button>
              </Box>
              <Stack spacing={2}>
                {template.variables.map((variable) => (
                  <TextField
                    key={variable}
                    label={variable}
                    value={variableValues[variable] || ''}
                    onChange={(e) =>
                      setVariableValues({
                        ...variableValues,
                        [variable]: e.target.value,
                      })
                    }
                    size="small"
                    fullWidth
                    placeholder={`Enter ${variable}`}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {template.variables.length === 0 && (
            <Alert severity="info">
              This template has no variables. The preview will be exactly as shown.
            </Alert>
          )}

          <Divider />

          {/* Preview */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Preview
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'background.paper',
                  borderRadius: 1,
                  boxShadow: 1,
                }}
              >
                {/* Priority Indicator */}
                {template.priority === NotificationPriority.CRITICAL && (
                  <Box
                    sx={{
                      height: 4,
                      bgcolor: 'error.main',
                      borderRadius: '4px 4px 0 0',
                      mb: 2,
                      mx: -2,
                      mt: -2,
                    }}
                  />
                )}
                {template.priority === NotificationPriority.HIGH && (
                  <Box
                    sx={{
                      height: 4,
                      bgcolor: 'warning.main',
                      borderRadius: '4px 4px 0 0',
                      mb: 2,
                      mx: -2,
                      mt: -2,
                    }}
                  />
                )}

                {/* Title */}
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {previewTitle}
                </Typography>

                {/* Message */}
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-wrap' }}
                >
                  {previewMessage}
                </Typography>

                {/* Timestamp */}
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mt: 2 }}
                >
                  Just now
                </Typography>
              </Box>
            </Paper>
          </Box>

          {/* Raw Template */}
          <Box>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Raw Template
            </Typography>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'grey.100',
                border: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="body2" fontFamily="monospace" gutterBottom>
                <strong>Title:</strong> {template.title}
              </Typography>
              <Typography variant="body2" fontFamily="monospace" sx={{ whiteSpace: 'pre-wrap' }}>
                <strong>Message:</strong> {template.message}
              </Typography>
            </Paper>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
